/**
 * Нижняя навигация. Четыре вкладки на роль (раздел 7 плана).
 *
 * Плавающая «таблетка», а не полоса во всю ширину: бумага под ней остаётся
 * видна, и панель читается как предмет, лежащий поверх дневника. Под активной
 * вкладкой мягкая капля, значок подпрыгивает при переключении.
 *
 * Бейдж на вкладке — единственный способ, которым приложение просит внимания
 * внутри себя: неподтверждённые расходы, открытый спор, незакрытая смена.
 */

import clsx from 'clsx'
import { NavLink } from 'react-router-dom'

import type { CoachKey } from '@/shared/api/hints'
import { haptic } from '@/shared/telegram'
import { Icon, type IconName } from '@/shared/ui/Icon'

export interface TabItem {
  to: string
  label: string
  icon: IconName
  badge?: number
  /** Якорь для подсказки первого входа (shared/ui/CoachMark.tsx). */
  coach?: CoachKey
}

export function TabBar({ items }: { items: TabItem[] }) {
  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3"
      style={{ paddingBottom: 'calc(var(--safe-bottom) + var(--tabbar-gap))' }}
    >
      <ul
        className={clsx(
          // отступ по бокам держит nav, а не ul: иначе mx-3 и mx-auto спорят
          'pointer-events-auto mx-auto flex max-w-[440px] items-stretch',
          'gap-0.5 rounded-[var(--radius-sheet)] p-1.5',
          'bg-surface/88 border-line/70 border shadow-[var(--shadow-tab)] backdrop-blur-xl',
        )}
        style={{ height: 'var(--tabbar-height)' }}
      >
        {items.map((item) => (
          <li key={item.to} data-coach={item.coach} className="min-w-0 flex-1">
            <NavLink
              to={item.to}
              onClick={() => haptic('select')}
              className={({ isActive }) =>
                clsx(
                  'group relative flex h-full flex-col items-center justify-center gap-[3px]',
                  'press rounded-[var(--radius-tile)] text-micro font-medium',
                  isActive ? 'text-accent bg-accent-soft/70' : 'text-text-muted',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className="flex leading-none"
                    // подпрыгивает ровно в момент, когда вкладка стала активной
                    style={isActive ? { animation: 'hop 460ms var(--ease-spring)' } : undefined}
                  >
                    <Icon name={item.icon} size={22} />
                  </span>
                  <span className="max-w-full truncate px-0.5">{item.label}</span>
                  {item.badge ? (
                    <span
                      className={clsx(
                        'bg-warm-ink pop absolute top-1 right-1.5 min-w-[17px] rounded-full',
                        'px-1 text-micro leading-[17px] font-bold text-on-warm',
                      )}
                    >
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  ) : null}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
